import React, { useEffect, useState } from 'react'
import { ActivityIndicator, StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'
import { Button, Text, TextInput } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import {
  useAdminOrganizerCandidates,
  useAdminOrganizerInvitations,
  useCancelOrganizerInvitation,
  useGiveOrganizerRole,
  useInviteOrganizer,
  type OrganizerCandidate,
} from '@hooks'
import { apiErrorCode, apiErrorParam, formatAdminDate, looksLikeEmail, showApiErrorToast } from '@utils'
import type { AdminStackParamList } from '../../../navigation/navigator/AdminStackNavigator'
import { AdminPage } from './components/AdminPage'
import {
  EMPTY_GRANT_OPTIONS,
  GrantOptions,
  type GrantOptionsValue,
  toGrantInput,
} from './components/GrantOptions'
import { PersonAvatar } from './components/PersonAvatar'

type Props = NativeStackScreenProps<AdminStackParamList, 'AdminAddOrganizer'>

/** Adds an organizer without waiting for a request: pick an account, or invite an email. */
export const AdminAddOrganizer: React.FC<Props> = ({ navigation }) => {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyles(theme)

  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])
  const candidates = useAdminOrganizerCandidates(debounced)
  const found = debounced.length >= 2 ? (candidates.data?.data ?? []) : []

  const [chosen, setChosen] = useState<OrganizerCandidate | null>(null)
  const [options, setOptions] = useState<GrantOptionsValue>(EMPTY_GRANT_OPTIONS)
  const give = useGiveOrganizerRole()

  const [email, setEmail] = useState('')
  const [inviteOptions, setInviteOptions] = useState<GrantOptionsValue>(EMPTY_GRANT_OPTIONS)
  // Set when the invited email already has an account: we then offer to give the role instead.
  const [existing, setExisting] = useState<{ accountId: string } | null>(null)
  const invite = useInviteOrganizer()
  const invitations = useAdminOrganizerInvitations()
  const cancel = useCancelOrganizerInvitation()

  // A pasted email in the search box prefills the invitation field.
  useEffect(() => {
    if (looksLikeEmail(debounced) && email === '') setEmail(debounced)
  }, [debounced, email])

  const grantInput = toGrantInput(options)
  const inviteInput = toGrantInput(inviteOptions)
  const emailValid = looksLikeEmail(email)

  const sendInvitation = () => {
    if (!inviteInput || !emailValid) return
    setExisting(null)
    invite.mutate(
      { email: email.trim(), ...inviteInput },
      {
        onSuccess: () => {
          setEmail('')
          setInviteOptions(EMPTY_GRANT_OPTIONS)
        },
        onError: (error) => {
          const accountId = apiErrorParam(error, 'accountId')
          if (apiErrorCode(error) === 'Events.OrganizerInvitation.AccountExists' && accountId) {
            setExisting({ accountId })
          } else {
            showApiErrorToast(t, error)
          }
        },
      },
    )
  }

  const giveToExisting = () => {
    if (!existing || !inviteInput) return
    give.mutate(
      { accountId: existing.accountId, ...inviteInput },
      {
        onSuccess: () => {
          setExisting(null)
          setEmail('')
          navigation.navigate('AdminOrganizerDetail', { accountId: existing.accountId })
        },
      },
    )
  }

  return (
    <AdminPage
      title={t('admin.add.title')}
      onBack={() => navigation.navigate('AdminOrganizers')}
      active="organizers"
    >
      <View style={styles.card}>
        <Text type="subtitleBold">{t('admin.add.searchTitle')}</Text>
        <Text type="bodySmall" color={theme.colors.greyBlue}>
          {t('admin.add.searchHint')}
        </Text>
        <TextInput
          testID="admin-add-search"
          iconLeft="search"
          placeholder={t('admin.add.searchPlaceholder')}
          value={search}
          onChangeText={(value) => {
            setSearch(value)
            setChosen(null)
          }}
          autoCapitalize="none"
        />
        {debounced.length >= 2 && candidates.isFetching && found.length === 0 ? (
          <ActivityIndicator color={theme.colors.primary} />
        ) : null}
        {debounced.length >= 2 && !candidates.isFetching && found.length === 0 ? (
          <Text type="bodySmall" color={theme.colors.greyBlue} testID="admin-add-none">
            {t('admin.add.noAccount')}
          </Text>
        ) : null}
        {found.map((person) => (
          <View key={person.accountId} style={styles.person} testID={`admin-candidate-${person.accountId}`}>
            <View style={styles.personRow}>
              <PersonAvatar
                uri={person.avatarUrl}
                firstName={person.firstName}
                lastName={person.lastName}
              />
              <View style={styles.personBody}>
                <Text type="subtitleBold" numberOfLines={1}>
                  {`${person.firstName} ${person.lastName}`.trim() || person.email}
                </Text>
                <Text type="bodySmall" color={theme.colors.greyBlue} numberOfLines={1}>
                  {person.email}
                </Text>
                {person.jobTitle ? (
                  <Text type="bodyExtraSmall" color={theme.colors.greyBlue} numberOfLines={1}>
                    {person.jobTitle}
                  </Text>
                ) : null}
              </View>
            </View>
            {person.isOrganizer ? (
              <Text type="bodySmallBold" color={theme.colors.green} testID={`admin-candidate-${person.accountId}-already`}>
                {t('admin.add.alreadyOrganizer')}
              </Text>
            ) : chosen?.accountId === person.accountId ? (
              <>
                <GrantOptions value={options} onChange={setOptions} testIDPrefix="admin-give" />
                <Button
                  testID="admin-give-confirm"
                  title={t('admin.add.give')}
                  disabled={!grantInput || give.isPending}
                  onPress={() =>
                    grantInput &&
                    give.mutate(
                      { accountId: person.accountId, ...grantInput },
                      {
                        onSuccess: () =>
                          navigation.navigate('AdminOrganizerDetail', { accountId: person.accountId }),
                      },
                    )
                  }
                />
              </>
            ) : (
              <Button
                testID={`admin-candidate-${person.accountId}-give`}
                title={t('admin.add.give')}
                isSmall
                onPress={() => {
                  setChosen(person)
                  setOptions(EMPTY_GRANT_OPTIONS)
                }}
              />
            )}
          </View>
        ))}
      </View>

      <View style={styles.card}>
        <Text type="subtitleBold">{t('admin.add.inviteTitle')}</Text>
        <Text type="bodySmall" color={theme.colors.greyBlue}>
          {t('admin.add.inviteHint')}
        </Text>
        <TextInput
          testID="admin-invite-email"
          title={t('admin.add.emailLabel')}
          value={email}
          onChangeText={(value) => {
            setEmail(value)
            setExisting(null)
          }}
          keyboardType="email-address"
          autoCapitalize="none"
          hasError={email.trim() !== '' && !emailValid}
          errorMessage={email.trim() !== '' && !emailValid ? t('admin.add.emailInvalid') : undefined}
        />
        <GrantOptions value={inviteOptions} onChange={setInviteOptions} testIDPrefix="admin-invite" />
        {existing ? (
          <View style={styles.notice} testID="admin-invite-exists">
            <Text type="bodySmallBold" color={theme.colors.error}>
              {t('admin.add.accountExists')}
            </Text>
            <Button
              testID="admin-invite-give-instead"
              title={t('admin.add.give')}
              disabled={!inviteInput || give.isPending}
              onPress={giveToExisting}
            />
          </View>
        ) : (
          <Button
            testID="admin-invite-send"
            title={t('admin.add.invite')}
            disabled={!emailValid || !inviteInput || invite.isPending}
            onPress={sendInvitation}
          />
        )}
      </View>

      <View style={styles.card}>
        <Text type="subtitleBold">{t('admin.add.pendingTitle')}</Text>
        {invitations.isLoading ? (
          <ActivityIndicator color={theme.colors.primary} />
        ) : (invitations.data?.data ?? []).length === 0 ? (
          <Text type="bodySmall" color={theme.colors.greyBlue} testID="admin-invitations-empty">
            {t('admin.add.pendingEmpty')}
          </Text>
        ) : (
          invitations.data?.data.map((invitation) => (
            <View key={invitation.id} style={styles.invitation} testID={`admin-invitation-${invitation.id}`}>
              <View style={styles.personBody}>
                <Text type="bodySmallBold" numberOfLines={1}>
                  {invitation.email}
                </Text>
                <Text type="bodyExtraSmall" color={theme.colors.greyBlue} numberOfLines={2}>
                  {[
                    invitation.organizationName,
                    invitation.commissionRateBps !== null
                      ? t('admin.add.pendingRate', { rate: String(invitation.commissionRateBps / 100).replace('.', ',') })
                      : null,
                    t('admin.add.pendingSince', {
                      date: formatAdminDate(invitation.createdAt, currentLanguage),
                      name: invitation.invitedBy?.name || '—',
                    }),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
                <Text
                  type="bodyExtraSmallBold"
                  color={invitation.expired ? theme.colors.error : theme.colors.greyBlue}
                  testID={`admin-invitation-${invitation.id}-expiry`}
                >
                  {invitation.expired
                    ? t('admin.add.pendingExpired')
                    : t('admin.add.pendingExpiresOn', {
                        date: formatAdminDate(invitation.expiresAt, currentLanguage),
                      })}
                </Text>
              </View>
              <View style={styles.invitationActions}>
                {invitation.expired ? (
                  <Button
                    testID={`admin-invitation-${invitation.id}-resend`}
                    title={t('admin.add.resendInvitation')}
                    isSmall
                    disabled={invite.isPending}
                    onPress={() =>
                      invite.mutate({
                        email: invitation.email,
                        organizationName: invitation.organizationName ?? undefined,
                        commissionRateBps: invitation.commissionRateBps ?? undefined,
                        feePaidBy: invitation.feePaidBy ?? undefined,
                      })
                    }
                  />
                ) : null}
                <Button
                  testID={`admin-invitation-${invitation.id}-cancel`}
                  title={t('admin.add.cancelInvitation')}
                  unfilled
                  isSmall
                  disabled={cancel.isPending}
                  onPress={() => cancel.mutate(invitation.id)}
                />
              </View>
            </View>
          ))
        )}
      </View>
    </AdminPage>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    card: {
      gap: theme.spacing.md,
      padding: theme.spacing.lg,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey,
    },
    person: {
      gap: theme.spacing.md,
      paddingTop: theme.spacing.md,
      borderTopWidth: 1,
      borderTopColor: theme.colors.grey,
    },
    personRow: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.md },
    personBody: { flex: 1, minWidth: 0, gap: 2 },
    notice: {
      gap: theme.spacing.md,
      padding: theme.spacing.md,
      borderRadius: theme.border.medium,
      backgroundColor: theme.colors.errorSoft,
    },
    invitation: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.md,
      paddingTop: theme.spacing.md,
      borderTopWidth: 1,
      borderTopColor: theme.colors.grey,
    },
    invitationActions: { gap: theme.spacing.sm },
  })
